import Dialog from "../../General/ConfirmableDialog";
import React, { useState, ChangeEvent } from "react";
import {
  DialogTitle,
  DialogContent,
  Button,
  DialogActions,
  FormControl,
  Box,
  CircularProgress,
} from "@mui/material";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";

interface IFileUploadModalProps {
  open: boolean;
  onClose: () => void;
  truckForSale: any;
}

const FileUploadModal: React.FC<IFileUploadModalProps> = ({
  open,
  onClose,
  truckForSale,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const handleSnackbarClose = () => setStatus("idle");

  const handleFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    setFiles([...files, ...Array.from(event.target.files)]);
  };

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!files.length || !truckForSale) {
        throw new Error("No files selected or truck data is missing");
      }
      setLoading(true);

      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });

      const response = await axios.post(
        `${apiVariable}/truckForSale/uploadMultiple/${truckForSale.id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      setStatus("success");
      setLoading(false);
      setFiles([]);
      queryClient.invalidateQueries({
        queryKey: ["truckForSaleImages", truckForSale.id],
      });
      onClose();
    },
    onError: (error: any) => {
      setStatus("error");
      setLoading(false);
      setErrorMessage(
        error?.response?.data || "Error occurred during file upload."
      );
    },
  });

  const handleSubmit = () => {
    uploadMutation.mutate();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          Upload Images for {truckForSale?.make} {truckForSale?.model}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ marginBottom: 2 }}>
            <FormControl fullWidth>
              <Button
                variant="outlined"
                component="label"
                sx={{ marginBottom: 2 }}
              >
                Select Files
                <input
                  hidden
                  multiple
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleFilesChange}
                />
              </Button>
              {files.map((file, index) => (
                <div key={index}>{file.name}</div>
              ))}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
      <StatusSnackbar
        status={status}
        successMessage="Files uploaded successfully!"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default FileUploadModal;
